import React, {useState, useEffect} from "react";
import dayjs from "dayjs";
import Big from "big.js";
import {useOutletContext} from "react-router-dom";
import {useCurrentUser} from "../../utils/CurrentUserContext";
import {useCubeQuery} from "../../utils/hooks/useCubeQuery";
import Table from "../../components/lists/Table/Table";
import {nestWebsiteTD} from "../../utils/helperfuctions";
import useCubeClient from "../../utils/cubejs-client";
import {Modal, Select} from "antd";
import DateSelect from "../../components/buttons/DateSelect";
import {useDateRange} from "../../utils/date-utils";
import {CompanyType} from "../../__generated__/graphql";
import TopStats from "./ReportingComponents/TopStats";
import ActionButton from "../../components/lists/Table/shared/ActionButton";
import {TrashIcon, SquaresPlusIcon} from "@heroicons/react/24/outline";
import TrafficDriverModal from "../../components/modals/TrafficDriverModal";
import {useMutation} from "@apollo/client";
import {CREATE_TRAFFIC_DRIVER, DELETE_TRAFFIC_DRIVER} from "../../utils/api/trafficDriver";
import Cookies from 'js-cookie';
import Spinner from "../../components/icons/Spinner";

const getAllColumns = (showTD, onCreate, onDelete) => [
  {
    title: showTD ? "Traffic driver" : "Website",
    dataIndex: "name",
    key: "name",
    fixed: "left",
    render: (text) => text || '-', // Display '-' if the value is null or undefined
  },
  {
    title: "Impressions",
    dataIndex: "tdImpressions",
    key: "impressions",
    sorter: (a, b) => a.tdImpressions - b.tdImpressions,
    render: (text) => text !== null && text !== undefined
      ? parseInt(text).toLocaleString() // Add thousand separators
      : '-',
  },
  {
    title: "Clicks",
    dataIndex: "tdClicks",
    key: "clicks",
    sorter: (a, b) => a.tdClicks - b.tdClicks,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "Reads",
    dataIndex: "reads",
    key: "reads",
    sorter: (a, b) => a.reads - b.reads,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "Revenue",
    dataIndex: "publisherRevenue",
    key: "revenue",
    sorter: (a, b) => a.publisherRevenue - b.publisherRevenue,
    render: (text) => text !== null && text !== undefined
      ? parseFloat(text).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) // Add thousand separators and format to 2 decimal places
      : '-',
  },
  {
    title: "Clickouts",
    dataIndex: "click_outs",
    key: "click_outs",
    sorter: (a, b) => a.click_outs - b.click_outs,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "Read time",
    dataIndex: "read_time",
    key: "read_time",
    sorter: (a, b) => a.read_time - b.read_time,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "Conversions",
    dataIndex: "conversions",
    key: "conversions",
    sorter: (a, b) => a.conversions - b.conversions,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "eCPM",
    dataIndex: "eCpm",
    key: "eCPM",
    sorter: (a, b) => a.eCpm - b.eCpm,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "CTR",
    dataIndex: "ctr",
    key: "ctr",
    sorter: (a, b) => a.ctr - b.ctr,
    render: (text) => text !== null && text !== undefined ? `${text}%` : '-', // Add '%' and handle null
  },
  {
    title: "Impression to reads",
    dataIndex: "imps_reads",
    key: "imps_reads",
    sorter: (a, b) => a.imps_reads - b.imps_reads,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    title: "CPA",
    dataIndex: "cpa",
    key: "cpa",
    sorter: (a, b) => a.cpa - b.cpa,
    render: (text) => text !== null && text !== undefined ? text : '-',
  },
  {
    key: "actions",
    fixed: "right",
    render: (_, row) => {
      if (row.websiteId) {
        return (
          <ActionButton
            tooltip="Create traffic driver"
            row={row}
            onClick={onCreate}
            Icon={SquaresPlusIcon}
          />
        );
      } else {
        // Optionally render delete button
      }
    },
  },
];


const ReportNew = () => {
  const {user} = useCurrentUser();
  const {campaign} = useOutletContext();
  const [showTD, setShowTD] = useState();

  const [createModalData, setCreateModalData] = useState(false);
  
  // Using the new useDateRange hook with 300ms debounce
  const {
    dateRange,
    handleDateChange: handleDate,
    formattedDateRange
  } = useDateRange(
    [dayjs().add(-730, "d"), dayjs()], // Default to last 2 years
    300 // 300ms debounce
  );
  
  const [createTrafficDriver] = useMutation(CREATE_TRAFFIC_DRIVER);
  const [deleteTrafficDriver] = useMutation(DELETE_TRAFFIC_DRIVER, {
    // refetchQueries: ["TeaserMany"],
  });
  const cubejsApi = useCubeClient()
  const reportType = campaign.ownerCompanyId !== user.companyId ? "Owner" : user.company.type;

  const [visibleColumns, setVisibleColumns] = useState(['name', 'tdImpressions', 'tdClicks', 'reads']);

  const aggPublisherFilters = user.company.type === CompanyType.Publisher && campaign.ownerCompanyId !== user.companyId
    ? [{
      member: "aggregateTdLevel.publisherId",
      operator: "equals",
      values: [user.companyId],
    }]
    : [];

  const publisherFilters = user.company.type === CompanyType.Publisher && campaign.ownerCompanyId !== user.companyId ? [{
    member: "Params_Event.contimoPublisherId",
    operator: "equals",
    values: [user.companyId],
  },
  ] : []


  const {
    resultSet: aggData,
    error: aggError,
    isLoading: aggIsLoading,
    refetch
  } = useCubeQuery(
    {
      dimensions: [
        "aggregateTdLevel.websiteId",
        "aggregateTdLevel.publicationId",
        "aggregateTdLevel.websiteName",
        "aggregateTdLevel.campaignBudget",
        "aggregateTdLevel.trafficDriverId",
        "aggregateTdLevel.tdHeadline",
        "aggregateTdLevel.tdImage",
        "aggregateTdLevel.campaignType",
      ],
      measures: [
        "aggregateTdLevel.tdImpressions",
        "aggregateTdLevel.tdClicks",
        "aggregateTdLevel.reads",
        "aggregateTdLevel.totalCost",
        "aggregateTdLevel.publisherRevenue",
      ],
      order: {
        "aggregateTdLevel.websiteId": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.campaignId",
          operator: "equals",
          values: [campaign._id],
        },
        {
          member: "aggregateTdLevel.websiteId",
          operator: "set",
        },
        {
          member: "aggregateTdLevel.publicationId",
          operator: "set",
        },
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals",
          values: ["true"],
        },
        ...aggPublisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          dateRange: formattedDateRange, // Using the formatted date range for consistency
        },
      ],
    },
    {cubejsApi}
  );


  //Total readtime
  const {
    resultSet: readResultSet,
    error: readError,
    isLoading: readIsLoading,
  } = useCubeQuery(
    {
      measures: [
        "Params_Event.averageTimeSpentOnPage",
        "Params_Event.cta_clicks",
      ],
      filters: [
        {
          member: "Params_Event.contimoCampaignId",
          operator: "equals",
          values: [campaign._id],
        },
        {
          member: "Params_Event.contimoWebsiteId",
          operator: "set",
        },
        ...publisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "Params_Event.eventDate",
          dateRange: formattedDateRange,
        },
      ],
    },
    {cubejsApi}
  );


  const {
    resultSet: pubResultSet,
    error: pubError,
    isLoading: pubIsLoading,
  } = useCubeQuery(
    {
      measures: [
        "Params_Event.averageTimeSpentOnPage",
        "Params_Event.cta_clicks",
      ],
      dimensions: ["Params_Event.contimoWebsiteId"],
      filters: [
        {
          member: "Params_Event.contimoCampaignId",
          operator: "equals",
          values: [campaign._id],
        },
        {
          member: "Params_Event.contimoWebsiteId",
          operator: "set",
        },
        ...publisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "Params_Event.eventDate",
          dateRange: formattedDateRange,
        },
      ],
    },
    {cubejsApi}
  );


  // Usage
  const result = aggData ? nestWebsiteTD(aggData.loadResponses[0].data) : {websiteData: [], totals: {}};
  const {websiteData, totals} = result;

  //const tdData = websiteData.reduce((acc, website) => ([...acc, ...(website.children || [])]), []);

  const handleCreateTrafficDriver = async (trafficDriver) => {
    await createTrafficDriver({variables: {record: trafficDriver}});
    await refetch();
  };

  const handleDeleteTrafficDriver = (row) => {
    Modal.confirm({
      title: "Are you sure?",
      content: `Are you sure you want to delete this traffic driver?`,
      okText: "Delete",
      okType: 'danger',
      onOk: async () => {
        await deleteTrafficDriver({variables: {id: row.trafficDriverId}});
        await refetch();
      },
    });
  };

  const saveColumnsToCookie = (columns) => {
    Cookies.set('selectedColumns', JSON.stringify(columns), {expires: 7});
  };

  const loadColumnsFromCookie = () => {
    const cookieColumns = Cookies.get('selectedColumns');
    return cookieColumns ? JSON.parse(cookieColumns) : ['name', 'tdImpressions', 'tdClicks', 'reads'];
  };

  useEffect(() => {
    const initialColumns = loadColumnsFromCookie();
    setVisibleColumns(initialColumns);
  }, []);

  useEffect(() => {
    saveColumnsToCookie(visibleColumns);
  }, [visibleColumns]);

  const columns = getAllColumns(showTD, setCreateModalData, handleDeleteTrafficDriver).filter(({
                                                                                                 key,
                                                                                                 dataIndex
                                                                                               }) => visibleColumns.includes(dataIndex) || key === 'actions');

  const options = [
    {label: "Website", value: "name", disabled: true},
    {label: "Impressions", value: "tdImpressions", disabled: true},
    {label: "Clicks", value: "tdClicks", disabled: true},
    {label: "Reads", value: "reads", disabled: true},
    {label: "Clickouts", value: "click_outs"},
    {label: "Conversions", value: "conversions"},
    {label: "Revenue", value: "publisherRevenue"},
    {label: "Read time", value: "read_time"},
    {label: "eCPM", value: "eCpm"},
    {label: "CTR", value: "ctr"},
    {label: "Impression to reads", value: "imps_reads"},
    {label: "CPA", value: "cpa"},
  ];

  if (websiteData.length === 0 || pubResultSet === null || readResultSet === null) {
    return <Spinner />;
  }


  const mergeWebsiteData = (paramsEventData, websiteData) => {
    const paramsEventMap = new Map();

    // Populate the map with contimoWebsiteId as key
    paramsEventData.forEach(event => {
      paramsEventMap.set(event["Params_Event.contimoWebsiteId"], {
        read_time: event["Params_Event.averageTimeSpentOnPageSeconds"] ? parseFloat(event["Params_Event.averageTimeSpentOnPageSeconds"]).toFixed(2) : null,
        click_outs: event["Params_Event.cta_clicks"],
      });
    });

    // Merge website data with params event data
    const mergedData = websiteData.map(website => {
      const event = paramsEventMap.get(website.websiteId) || {
        read_time: null,
        click_outs: null,
        ctr: null,
      };

      return {
        ...website,
        read_time: event.read_time,
        click_outs: event.click_outs,
        ctr: event.click_outs && website.tdImpressions ? (event.click_outs * 100 / website.tdImpressions).toFixed(2) : null,
        conversions: website.conversions ? parseFloat(website.conversions).toFixed(1) : null,
        eCpm: website.eCpm ? parseFloat(website.eCpm).toFixed(2) : null,
        cpa: website.cpa ? parseFloat(website.cpa).toFixed(2) : null,
        tdImpressions: website.tdImpressions ? parseInt(website.tdImpressions) : null,
      };
    });

    return mergedData;
  };


  const output = mergeWebsiteData(pubResultSet.loadResponse.results[0].data, websiteData)



  return (
    <div className="mb-4">
      <TopStats totalData={totals} ga={readResultSet.loadResponse.results[0].data[0]}/>
      <div className="flex mt-4 items-center">
        <DateSelect dateRange={dateRange} handleDate={handleDate}/>
        <Select
          className="mb-2"
          mode="multiple"
          options={options}
          value={visibleColumns}
          onChange={setVisibleColumns}
        />
      </div>
      <Table columns={columns} data={output} searchable={false}/>
      <TrafficDriverModal
        open={createModalData}
        defaultValues={{
          websiteId: createModalData?.websiteId,
          publicationId: createModalData?.publicationId,
          campaignId: campaign._id
        }}
        websites={createModalData ? [createModalData] : []}
        onCancel={() => setCreateModalData(null)}
        onOk={handleCreateTrafficDriver}
      />
    </div>
  );
};

export default ReportNew;
